/**
 * OpenRequestDetail – detaljvisning av en åpen samarbeid-forespørsel.
 * Mottaker kan akseptere (velge dato hvis flere), avslå med grunn.
 * Forespørrer kan avlyse.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, X, CalendarCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";

const STATUS_LABEL = {
  pending: "Venter på svar",
  accepted: "Akseptert",
  declined: "Avslått",
  cancelled: "Avlyst",
  expired: "Utløpt",
};

const STATUS_COLOR = {
  pending: "var(--cream-faint)",
  accepted: "var(--gold)",
  declined: "var(--magenta)",
  cancelled: "var(--cream-faint)",
  expired: "var(--cream-faint)",
};

export default function OpenRequestDetail() {
  const { user, loading: authLoading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pickedDate, setPickedDate] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [showDecline, setShowDecline] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/collab/open-requests/${id}`);
        setReq(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Fant ikke forespørselen.");
      } finally { setLoading(false); }
    })();
  }, [user, id]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_creator && !["admin", "owner"].includes(user.role)) {
    return <Navigate to="/skaper/samarbeid" replace />;
  }

  if (loading) return <Layout><div className="max-w-2xl mx-auto px-4 py-12 text-center text-[var(--cream-soft)]">Laster…</div></Layout>;
  if (!req) return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">Fant ikke forespørselen</h1>
        <Link to="/skaper/samarbeid?tab=open-inbox" className="btn-magenta">Til innboks</Link>
      </div>
    </Layout>
  );

  const isReceiver = req.target_id === user.id;
  const isSender = req.requester_id === user.id;
  const typeLbl = req.type === "other" ? (req.type_other || "ANNET") : req.type.toUpperCase();
  const platLbl = req.platform.toUpperCase();

  const accept = async () => {
    let date = pickedDate;
    if (!date && req.proposed_dates.length === 1) date = req.proposed_dates[0];
    if (!date) return toast.error("Velg en dato først.");
    setBusy(true);
    try {
      await api.post(`/collab/open-requests/${id}/accept`, { accepted_date: date });
      toast.success("Forespørsel akseptert. Dagen er lagt til kalenderen.");
      setReq({ ...req, status: "accepted", accepted_date: date });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke akseptere.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/collab/open-requests/${id}/decline`, { reason: declineReason.trim() || null });
      toast.success("Forespørsel avslått.");
      setReq({ ...req, status: "declined", decline_reason: declineReason.trim() || null });
      setShowDecline(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke avslå.");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Avlyse forespørselen?")) return;
    setBusy(true);
    try {
      await api.post(`/collab/open-requests/${id}/cancel`);
      toast.success("Forespørsel avlyst.");
      setReq({ ...req, status: "cancelled" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke avlyse.");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="open-req-page">
        <div className="mb-6">
          <Link to="/skaper/samarbeid?tab=open-inbox"
            className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Til innboks
          </Link>
        </div>

        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">ÅPEN FORESPØRSEL</div>
        <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-4">
          {isReceiver ? `Fra @${req.requester_username}` : `Til @${req.target_username}`}
        </h1>
        <div className="mb-4 inline-block px-2 py-1 border font-meta text-[10px] tracking-[0.15em] uppercase"
          style={{ borderColor: STATUS_COLOR[req.status], color: STATUS_COLOR[req.status] }}>
          {STATUS_LABEL[req.status] || req.status}
        </div>

        <div className="panel-tight p-4 mb-4">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-meta text-xs px-2 py-1 border border-[var(--magenta)] text-[var(--magenta)] uppercase tracking-wider">
              {typeLbl}
            </span>
            <span className="font-fraunces italic text-xl text-[var(--cream)]">på {platLbl}</span>
          </div>
        </div>

        {/* Datoer */}
        <div className="panel-tight p-4 mb-4">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">ØNSKET DAG</div>
          {req.status === "accepted" && req.accepted_date ? (
            <div className="font-fraunces italic text-lg text-[var(--gold)]">
              ✓ Bekreftet: {new Date(req.accepted_date).toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          ) : req.proposed_dates.length === 0 ? (
            <p className="text-sm text-[var(--cream-soft)] italic">Ingen dato foreslått — du sjekker din kalender.</p>
          ) : req.proposed_dates.length === 1 ? (
            <div className="font-fraunces italic text-lg text-[var(--cream)]">
              {new Date(req.proposed_dates[0]).toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          ) : (
            <div className="space-y-1.5">
              <p className="text-xs text-[var(--cream-soft)]">Velg én av datoene:</p>
              {req.proposed_dates.map((d) => (
                <label key={d} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="pick-date" value={d}
                    checked={pickedDate === d} onChange={(e) => setPickedDate(e.target.value)}
                    disabled={!isReceiver || req.status !== "pending"}
                    className="accent-[var(--magenta)]" data-testid={`open-req-radio-${d}`} />
                  <span className="text-sm text-[var(--cream)]">
                    {new Date(d).toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" })}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="panel-tight p-4 mb-4">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">MELDING</div>
          <p className="text-[var(--cream)] font-manrope whitespace-pre-line text-sm leading-relaxed">{req.message}</p>
          {req.deal_offer && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--gold)] mb-1">DEALEN</div>
              <p className="text-sm text-[var(--cream-soft)]">{req.deal_offer}</p>
            </div>
          )}
          <div className="text-[10px] font-meta text-[var(--cream-faint)] mt-3">
            Sendt {new Date(req.created_at).toLocaleString("nb-NO")}
          </div>
        </div>

        {req.status === "declined" && req.decline_reason && (
          <div className="panel-tight p-4 mb-4 border-[var(--magenta)]/40">
            <div className="font-meta text-[10px] tracking-[0.15em] text-[var(--magenta)] mb-1">GRUNN FOR AVSLAG</div>
            <p className="text-sm text-[var(--cream-soft)]">{req.decline_reason}</p>
          </div>
        )}

        {/* Handlinger */}
        {isReceiver && req.status === "pending" && (
          <div className="mt-6 space-y-2">
            <div className="flex flex-wrap gap-2">
              <button onClick={accept} disabled={busy}
                className="btn-magenta inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="open-req-accept">
                <Check className="h-4 w-4" /> Aksepter
              </button>
              <button onClick={() => setShowDecline(!showDecline)} disabled={busy}
                className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="open-req-decline-toggle">
                <X className="h-4 w-4" /> Avslå
              </button>
              <button onClick={() => navigate(`/meldinger?til=${req.requester_username}`)} type="button"
                className="btn-ghost inline-flex items-center gap-2"
                data-testid="open-req-dm">
                Send melding
              </button>
            </div>
            {req.proposed_dates.length === 0 && (
              <div className="pt-3">
                <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-faint)] block mb-1">
                  DIN FORESLÅTTE DATO
                </label>
                <input type="date" value={pickedDate} onChange={(e) => setPickedDate(e.target.value)}
                  className="input-editorial w-full max-w-xs" data-testid="open-req-your-date" />
                <p className="text-[10px] font-meta text-[var(--cream-faint)] mt-1">
                  Trykk «Aksepter» over etter at du har valgt dato.
                </p>
              </div>
            )}
            {showDecline && (
              <div className="pt-3 space-y-2">
                <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                  rows={2} maxLength={300} placeholder="Grunn for avslag (valgfritt)"
                  className="input-editorial w-full font-manrope text-sm" data-testid="open-req-decline-reason" />
                <button onClick={decline} disabled={busy}
                  className="btn-magenta text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="open-req-decline-confirm">
                  Bekreft avslag
                </button>
              </div>
            )}
          </div>
        )}

        {isSender && req.status === "pending" && (
          <div className="mt-6">
            <button onClick={cancel} disabled={busy}
              className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="open-req-cancel">
              <X className="h-4 w-4" /> Avlys forespørsel
            </button>
          </div>
        )}

        {req.status === "accepted" && (
          <div className="mt-6 panel-tight p-4 border-[var(--gold)]/40 bg-[var(--gold)]/5">
            <div className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5 text-[var(--gold)]" />
              <span className="font-fraunces italic text-[var(--cream)]">
                Avtale bekreftet — dagen er markert i begge kalendrene.
              </span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
